import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { showErrorToast, showSuccessToast } from 'components/Toast';
import BaseInput from 'components/Inputs/BaseInput';
import SolidButton from 'components/SolidButton';

import { promoOnly, numberOnly } from 'utils/inputValidation';
import { REQUIRED_RULE } from 'utils/constants';
import { formatDate } from 'utils/functions';
import GeneralApi from 'utils/generalApi';
import { IPromo } from 'utils/interfaces';
import { useAuth } from 'contexts/Auth';

export default function PromoModalContent({
  title,
  editData,
  onClose,
  onSuccess,
}: {
  title: string;
  editData?: IPromo;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      ...editData,
      from: editData ? formatDate(new Date(editData.from), 'yyyy-MM-dd', false) : undefined,
      to: editData ? formatDate(new Date(editData.to), 'yyyy-MM-dd', false) : undefined,
    },
  });
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [loading, setLoading] = useState(false);

  const submitAction = async (data: any) => {
    setLoading(true);
    let result: any = await (editData
      ? generalApi.put('/promo', {
          ...data,
          promoId: editData._id,
          from: `${data.from}T00:00`,
          to: `${data.to}T23:59`,
        })
      : generalApi.post('/promo', {
          ...data,
          from: `${data.from}T00:00`,
          to: `${data.to}T23:59`,
        }));
    setLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast(editData ? 'Cambios guardados' : 'Promoción creada');
    onSuccess();
  };

  const buildBaseInput = (
    label: string,
    type: React.HTMLInputTypeAttribute,
    name: string,
    rules: any,
    props: any
  ) => {
    return (
      <BaseInput
        label={label}
        type={type}
        name={name}
        register={register}
        errors={errors}
        watch={watch}
        rules={rules}
        {...props}
      />
    );
  };

  return (
    <form onSubmit={handleSubmit(submitAction)}>
      <div className="bg-primary-600 rounded-t-lg px-4 pb-4 sm:flex sm:items-start sm:p-6 sm:py-4">
        <div className="mt-3 text-center sm:mt-0 sm:text-left">
          <h3 className="text-lg font-medium leading-6 text-white" id="modal-headline">
            {title || 'Confirmar acción'}
          </h3>
        </div>
      </div>
      <div onSubmit={handleSubmit(submitAction)} className="space-y-4 px-4 py-5 sm:p-6">
        {buildBaseInput('Nombre', 'text', 'name', REQUIRED_RULE, {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            setValue('name', promoOnly(e.target.value)),
        })}
        {buildBaseInput('Desde', 'date', 'from', REQUIRED_RULE, {})}
        {buildBaseInput('Hasta', 'date', 'to', REQUIRED_RULE, {})}
        {buildBaseInput('spCompra', 'text', 'sp_compra', REQUIRED_RULE, {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            setValue('sp_compra', numberOnly(e.target.value)),
        })}
        {buildBaseInput('spVenta', 'text', 'sp_venta', REQUIRED_RULE, {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            setValue('sp_venta', numberOnly(e.target.value)),
        })}
        {buildBaseInput('Límite por persona', 'text', 'per_person', REQUIRED_RULE, {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            setValue('per_person', numberOnly(e.target.value)),
        })}
        {buildBaseInput('Límite de usos', 'text', 'per_use', REQUIRED_RULE, {
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            setValue('per_use', numberOnly(e.target.value)),
        })}
      </div>
      <div className="bg-gray-50 rounded-b-lg px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
        <SolidButton
          className="w-auto sm:ml-3"
          isLoading={loading}
          onClick={() => handleSubmit(submitAction)}
        >
          {editData ? 'Guardar cambios' : 'Crear'}
        </SolidButton>
        <button
          type="button"
          onClick={onClose}
          className="focus:ring-primary-500 mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
